import React, { useState } from 'react';
import { motion } from 'framer-motion';
import appConfig from '../../config/appConfig';
import useInView from '../../hooks/useInView';
import styles from './DownloadSection.module.css';

function Reveal({ children, delay = 0 }) {
  const { ref, inView } = useInView({ threshold: 0.15, rootMargin: '0px 0px -12% 0px', once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 18, filter: 'blur(10px)' }}
      animate={inView ? { opacity: 1, y: 0, filter: 'blur(0px)' } : undefined}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function detectEnv() {
  if (typeof navigator === 'undefined') {
    return { isInstagram: false, isIOS: false };
  }
  const ua = navigator.userAgent || '';
  return {
    isInstagram: /Instagram/i.test(ua),
    isIOS: /iPhone|iPad|iPod/i.test(ua),
  };
}

/** Build the real href - Instagram iOS blocks apps.apple.com unless we exit to Safari first. */
function buildHref(platform, storeUrl, storeAppUrl, env) {
  if (!storeUrl) return undefined;

  if (platform === 'iOS') {
    // User-tapped instagram://extbrowser opens Safari with the App Store URL (no intermediate page).
    if (env.isInstagram && env.isIOS) {
      return `instagram://extbrowser/?url=${encodeURIComponent(storeUrl)}`;
    }
    if (env.isIOS && storeAppUrl) return storeAppUrl;
    return storeUrl;
  }

  return storeUrl;
}

function AppCard({ platform, available, storeUrl, storeAppUrl, qrCodeUrl, env }) {
  const href = available ? buildHref(platform, storeUrl, storeAppUrl, env) : undefined;

  const content = (
    <>
      <div className={styles.appHeader}>
        <div className={styles.appIcon} aria-hidden="true">
          {platform === 'iOS' ? '' : '⟡'}
        </div>
        <div>
          <div className={styles.appName}>{platform}</div>
        </div>
        <div className={`${styles.badge} ${available ? styles.badgeLive : styles.badgeSoon}`}>
          {available ? 'Click to download' : 'COMING SOON'}
        </div>
      </div>

      <div className={styles.appBody}>
        <p className={styles.pSmall}>
          {available
            ? 'Scan to open the store listing or share with a friend.'
            : 'We’re crafting a safer, more intentional way to connect. Join the waitlist soon.'}
        </p>

        <div className={styles.qrRow}>
          {available && qrCodeUrl ? (
            <img className={styles.qr} src={qrCodeUrl} alt="" />
          ) : null}
        </div>
      </div>
    </>
  );

  // Real <a> wrapping the whole card - Instagram WebView often ignores absolute overlay links.
  if (available && href) {
    return (
      <a
        className={`${styles.appCard} ${styles.appCardInteractive}`}
        href={href}
        rel="noopener noreferrer"
        aria-label={`Download Timeline for ${platform}`}
      >
        {content}
      </a>
    );
  }

  return <div className={styles.appCard}>{content}</div>;
}

export default function DownloadSection({
  eyebrow = 'App availability',
  title = 'Built for iOS and Android.',
  desc = 'Scan the QR code to download the app once available.',
  animate = true,
  className = '',
}) {
  // Detect once on mount synchronously so Instagram gets the correct href immediately
  const [env] = useState(() => detectEnv());

  const iosCard = (
    <AppCard
      platform="iOS"
      available={appConfig.ios?.launched}
      storeUrl={appConfig.ios?.storeUrl}
      storeAppUrl={appConfig.ios?.storeAppUrl}
      qrCodeUrl={appConfig.ios?.qrCodeUrl}
      env={env}
    />
  );

  const androidCard = (
    <AppCard
      platform="Android"
      available={appConfig.android?.launched}
      storeUrl={appConfig.android?.storeUrl}
      qrCodeUrl={appConfig.android?.qrCodeUrl}
      env={env}
    />
  );

  return (
    <div className={`${styles.root} ${className}`.trim()}>
      <div className={styles.sectionTitle}>
        <div className={styles.eyebrow}>{eyebrow}</div>
        <h2 className={styles.h2}>{title}</h2>
        {desc ? <p className={styles.p}>{desc}</p> : null}
      </div>

      <div className={`${styles.grid2} ${styles.storeGrid}`}>
        {animate ? (
          <>
            <Reveal>{iosCard}</Reveal>
            <Reveal delay={0.06}>{androidCard}</Reveal>
          </>
        ) : (
          <>
            {iosCard}
            {androidCard}
          </>
        )}
      </div>
    </div>
  );
}
